import * as swcHelpers from '@swc/helpers';
import identity from 'lodash/identity';
import isEmpty from 'lodash/isEmpty';
import pickBy from 'lodash/pickBy';
import { Types } from '@grandvisionhq/graphql';
import { isNil } from '@grandvisionhq/utils';
import { convertToGqlDateFormat } from './convertToGqlDateFormat.js';
import { isDelivery, isDeliveryExpress, isNearestPickup, isPickupStore, isPickupPoint } from './shipping-method.js';

const createAddress = (data)=>{
    const { title , salutation , firstName , lastName , middleName , additionalAddressInfo , streetName , streetNumber , additionalStreetInfo , city , company , postalCode , region , state , phone , email , externalId , countryCode ,  } = data;
    const address = swcHelpers.objectSpread({
        title,
        salutation,
        firstName,
        lastName,
        middleName
    }, !isEmpty(additionalAddressInfo) ? {
        additionalAddressInfo
    } : null, {
        streetName,
        streetNumber: streetNumber !== null && streetNumber !== void 0 ? streetNumber : undefined
    }, !isEmpty(additionalStreetInfo) ? {
        additionalStreetInfo
    } : null, {
        company: company !== null && company !== void 0 ? company : undefined,
        countryCode,
        city,
        postalCode,
        state,
        region,
        phone,
        email,
        externalId
    });
    return pickBy(address, identity);
};
const convertStoreToAddress = (store)=>{
    const { code , name , streetName , streetNumber , additionalStreetInfo , postalCode , town , emailAddress , phone ,  } = store;
    return {
        externalId: code.toString() || '',
        company: name,
        city: town,
        email: emailAddress !== null && emailAddress !== void 0 ? emailAddress : undefined,
        phone,
        streetName,
        streetNumber: streetNumber !== null && streetNumber !== void 0 ? streetNumber : undefined,
        additionalStreetInfo: additionalStreetInfo !== null && additionalStreetInfo !== void 0 ? additionalStreetInfo : undefined,
        postalCode: postalCode !== null && postalCode !== void 0 ? postalCode : undefined
    };
};
const convertPickupPointToAddress = (pickupPoint)=>{
    const { code , name , streetName , streetNumber , additionalStreetInfo , postalCode , town  } = pickupPoint;
    return {
        externalId: code.toString() || '',
        company: name,
        city: town,
        streetName,
        streetNumber: streetNumber !== null && streetNumber !== void 0 ? streetNumber : undefined,
        additionalStreetInfo: additionalStreetInfo !== null && additionalStreetInfo !== void 0 ? additionalStreetInfo : '',
        postalCode: postalCode !== null && postalCode !== void 0 ? postalCode : undefined
    };
};
var InvoiceFieldMapForPayload;
(function(InvoiceFieldMapForPayload) {
    InvoiceFieldMapForPayload["secondaryEmail"] = 'certifiedEmail';
    InvoiceFieldMapForPayload["customerCode"] = "customerCode";
    InvoiceFieldMapForPayload["fiscalNumber"] = 'customerCode';
    InvoiceFieldMapForPayload["recipientCode"] = 'certifiedRecipientCode';
    InvoiceFieldMapForPayload["customerVatId"] = 'vatId';
    InvoiceFieldMapForPayload["insuranceProvider"] = "insuranceProvider";
    InvoiceFieldMapForPayload["insuranceNumber"] = "insuranceNumber";
})(InvoiceFieldMapForPayload || (InvoiceFieldMapForPayload = {}));
const invoiceResetData = {
    certifiedEmail: null,
    vatId: null,
    certifiedRecipientCode: '',
    customerCode: '',
    insuranceNumber: null,
    insuranceProvider: ''
};
const createInvoice = (invoiceDetails)=>{
    if (!invoiceDetails) {
        return true;
    }
    const invoice = Object.entries(invoiceDetails).reduce((result, [key, value])=>{
        return InvoiceFieldMapForPayload[key] ? Object.assign(result, swcHelpers.objectSpread({}, result, {
            [InvoiceFieldMapForPayload[key]]: value
        })) : result;
    }, {});
    return invoice;
};
const getCustomerDetailsPayload = ({ shippingDetails , countryCode , locale , isMultilingualEnabled , selectedPickupPoint , selectedShippingMethod , selectedStore  })=>{
    const customerDetailsPayload = swcHelpers.objectSpread({}, isMultilingualEnabled && locale ? {
        locale
    } : null);
    const { billingAddress , deliveryAddress , contactDetails , invoice , company  } = shippingDetails;
    let phone;
    // Extract and set customer details
    if (contactDetails) {
        phone = contactDetails.phone ? contactDetails.phone : null;
        let dateOfBirth = contactDetails.dateOfBirth || contactDetails.customerDateOfBirth || contactDetails.customerDOB || '';
        dateOfBirth = dateOfBirth === '' ? null : dateOfBirth;
        customerDetailsPayload.customerDetails = swcHelpers.objectSpread({
            email: contactDetails.email
        }, phone ? {
            phone
        } : null, contactDetails.secretIBAN ? {
            IBAN: contactDetails.secretIBAN
        } : null, dateOfBirth ? {
            dateOfBirth: convertToGqlDateFormat(dateOfBirth)
        } : null, {
            customerType: company ? Types.CheckoutCustomerType.B2B : Types.CheckoutCustomerType.B2C
        });
    }
    // Extract and set invoice details
    const invoiceDetails = invoice && createInvoice(invoice);
    if (invoiceDetails) {
        customerDetailsPayload.invoiceDetails = invoiceDetails;
    }
    // PT opcos deviate from the pattern. They have the customerVatId in ContactDetails.
    if (contactDetails === null || contactDetails === void 0 ? void 0 : contactDetails.customerVatId) {
        customerDetailsPayload.invoiceDetails = createInvoice(swcHelpers.objectSpread({}, invoice ? invoice : {}, {
            customerVatId: contactDetails === null || contactDetails === void 0 ? void 0 : contactDetails.customerVatId
        }));
    }
    /**
   * We should always have a billing address when delivery
   * Extract and set billing details
   */ //
    customerDetailsPayload.billingAddress = createAddress(swcHelpers.objectSpread({
        countryCode
    }, billingAddress));
    // Extract and set shipping details
    customerDetailsPayload.shippingAddress = deliveryAddress ? createAddress(swcHelpers.objectSpread({
        countryCode
    }, deliveryAddress)) : customerDetailsPayload.billingAddress;
    if (company) {
        customerDetailsPayload.invoiceDetails = createInvoice(company);
        customerDetailsPayload.billingAddress.company = company.name;
        customerDetailsPayload.shippingAddress.company = company.name;
    }
    if (customerDetailsPayload.invoiceDetails && customerDetailsPayload.invoiceDetails.certifiedEmail === '') {
        customerDetailsPayload.invoiceDetails.certifiedEmail = null;
    }
    if ((isDelivery(selectedShippingMethod.method) || isDeliveryExpress(selectedShippingMethod.method)) && phone) {
        customerDetailsPayload.shippingAddress.phone = phone;
    }
    if (isNearestPickup(selectedShippingMethod.method)) {
        // Use the billing address as the shipping address
        customerDetailsPayload.shippingAddress = swcHelpers.objectSpread({}, customerDetailsPayload.billingAddress, phone ? {
            phone
        } : null);
    }
    // If shipping method is pickup we need to fabricate an address with the data that we do have
    if (isPickupStore(selectedShippingMethod.method)) {
        if (selectedStore) {
            const storeAddress = convertStoreToAddress(selectedStore);
            customerDetailsPayload.shippingAddress = swcHelpers.objectSpread({}, storeAddress, {
                countryCode,
                firstName: customerDetailsPayload.billingAddress.firstName,
                lastName: customerDetailsPayload.billingAddress.lastName
            }, customerDetailsPayload.billingAddress.salutation ? {
                salutation: customerDetailsPayload.billingAddress.salutation
            } : {});
        }
    }
    if (isPickupPoint(selectedShippingMethod.method)) {
        // Set store as delivery address
        if (selectedPickupPoint) {
            const pickupPointAddress = convertPickupPointToAddress(selectedPickupPoint);
            customerDetailsPayload.shippingAddress = swcHelpers.objectSpread({}, pickupPointAddress, {
                countryCode,
                firstName: customerDetailsPayload.billingAddress.firstName,
                lastName: customerDetailsPayload.billingAddress.lastName
            }, customerDetailsPayload.billingAddress.salutation ? {
                salutation: customerDetailsPayload.billingAddress.salutation
            } : {});
        }
    }
    if (phone) {
        customerDetailsPayload.billingAddress.phone = phone;
    }
    if (isNil(customerDetailsPayload.invoiceDetails)) {
        customerDetailsPayload.invoiceDetails = invoiceResetData;
    } else {
        customerDetailsPayload.invoiceDetails = swcHelpers.objectSpread({}, invoiceResetData, customerDetailsPayload.invoiceDetails);
    }
    return customerDetailsPayload;
};

export { InvoiceFieldMapForPayload, convertPickupPointToAddress, convertStoreToAddress, createAddress, getCustomerDetailsPayload };
//# sourceMappingURL=get-customer-details-payload.js.map
